import { format } from "date-fns";
import { Download } from "lucide-react";
import { FieldPair, Section } from "./section";

interface Attachment {
  id: string;
  filename: string;
  url: string;
}

interface HousekeepingData {
  excludeFromMailout: boolean;
  isActive: boolean;
  cancelled: boolean;
  cancelledDate: string | null;
  finalSentConfirmed: boolean;
}

interface HistoryData {
  createdAt: string | null;
  updatedAt: string | null;
}

export function SectionNotes({ note }: { note: string | null }): React.ReactElement {
  return (
    <Section id="notes" title="Notes">
      {note ? (
        <p className="text-sm whitespace-pre-wrap">{note}</p>
      ) : (
        <p className="text-sm text-muted-foreground">No notes.</p>
      )}
    </Section>
  );
}

export function SectionAttachments({ attachments }: { attachments: Attachment[] }): React.ReactElement {
  return (
    <Section id="attachments" title="Attachments">
      {attachments.length > 0 ? (
        <div className="space-y-2">
          {attachments.map((att) => (
            <a
              key={att.id}
              href={att.url}
              className="flex items-center gap-3 rounded-md border p-3 hover:bg-accent/50 transition-colors"
              download
            >
              <Download className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{att.filename}</span>
            </a>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No attachments.</p>
      )}
    </Section>
  );
}

export function SectionSignature({ signatureUrl }: { signatureUrl: string | null }): React.ReactElement {
  return (
    <Section id="signature" title="Valuer Signature">
      {signatureUrl ? (
        <div className="h-24 w-48 rounded-md border bg-muted flex items-center justify-center text-sm text-muted-foreground">
          Signature preview
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No signature on file.</p>
      )}
    </Section>
  );
}

export function SectionHousekeeping({ data }: { data: HousekeepingData }): React.ReactElement {
  return (
    <Section id="housekeeping" title="Housekeeping">
      <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
        <FieldPair label="Exclude from Mailout" value={data.excludeFromMailout ? "Yes" : "No"} />
        <FieldPair label="Active" value={data.isActive ? "Yes" : "No"} />
        <FieldPair label="Cancelled" value={data.cancelled ? "Yes" : "No"} />
        <FieldPair
          label="Cancelled Date"
          value={data.cancelledDate ? format(new Date(data.cancelledDate), "d MMM yyyy") : "—"}
        />
        <FieldPair label="Final Sent Confirmed" value={data.finalSentConfirmed ? "Yes" : "No"} />
      </div>
    </Section>
  );
}

export function SectionHistory({ data }: { data: HistoryData }): React.ReactElement {
  return (
    <Section id="history" title="History">
      <div className="grid gap-4 grid-cols-2 sm:grid-cols-3">
        <FieldPair
          label="Date Added"
          value={data.createdAt ? format(new Date(data.createdAt), "d MMMM yyyy, h:mm a") : "—"}
        />
        <FieldPair
          label="Last Modified"
          value={data.updatedAt ? format(new Date(data.updatedAt), "d MMMM yyyy, h:mm a") : "—"}
        />
      </div>
    </Section>
  );
}

export function SectionFsl({ fslText }: { fslText: string | null }): React.ReactElement {
  return (
    <Section id="fsl" title="Fire and Emergency Levy Statement">
      {fslText ? (
        <p className="text-sm whitespace-pre-wrap">{fslText}</p>
      ) : (
        <p className="text-sm text-muted-foreground">No Fire and Emergency Levy statement.</p>
      )}
    </Section>
  );
}
